/**
 * Figure — the shared chart wrapper (spec §6): the chart title states the QUESTION the chart
 * answers, the caption carries the period, `role="group"` on the <figure> keeps the focusable
 * marks reachable (never `role="img"` on the SVG), a legend is mandatory for ≥ 2 series and a
 * "תצוגת טבלה" button swaps the SVG for a real <table> twin.
 */

import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../components/Icon.tsx';

export interface TableTwin {
  head: string[];
  rows: ReactNode[][];
}

export interface LegendItem {
  label: string;
  /** CSS colour, usually `var(--series-N)`. */
  color: string;
  hatch?: boolean;
  dim?: boolean;
  /** Render as a line swatch instead of a square. */
  line?: boolean;
}

export function Figure({
  question,
  period,
  summary,
  legend,
  table,
  children,
}: {
  question: string;
  period?: string;
  /** One-sentence takeaway added to the figure's accessible name. */
  summary?: string;
  legend?: LegendItem[];
  table?: TableTwin;
  children: ReactNode;
}) {
  const [showTable, setShowTable] = useState(false);
  const id = useId();
  return (
    <figure className="chart" role="group" aria-label={summary ? `${question}. ${summary}` : question}>
      <figcaption>
        <span>
          <span className="q">{question}</span>
          {period ? <span className="p"> · {period}</span> : null}
        </span>
        <span className="chart-tools">
          {table ? (
            <button type="button" className="btn ghost sm" aria-pressed={showTable} aria-controls={id} onClick={() => setShowTable((v) => !v)}>
              <Icon name="table" className="xs" />
              <span>{showTable ? 'תצוגת תרשים' : 'תצוגת טבלה'}</span>
            </button>
          ) : null}
        </span>
      </figcaption>
      <div id={id} hidden={showTable}>
        {children}
      </div>
      {table ? (
        <div className="tblwrap" hidden={!showTable}>
          <table>
            <thead>
              <tr>
                {table.head.map((cell, i) => (
                  <th key={cell} className={i ? 'n' : undefined} scope="col">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={ci ? 'n' : undefined}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {legend && legend.length ? (
        <div className="legend" aria-label="מקרא" hidden={showTable}>
          {legend.map((item) => (
            <span key={item.label}>
              <i
                className={`${item.hatch ? 'hatch' : ''}${item.line ? ' line' : ''}`}
                style={{ backgroundColor: item.color, opacity: item.dim ? 0.5 : 1 }}
              />
              {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </figure>
  );
}
